<template>
  <v-dialog v-model="dialog" max-width="480" persistent>
    <v-card class="save-dialog">
      <v-card-title class="dialog-title">
        <v-icon size="24" class="mr-2">mdi-content-save</v-icon>
        Save Calculation
      </v-card-title>

      <v-card-text class="dialog-content">
        <!-- No calculation error -->
        <v-alert
          v-if="!props.calculationId"
          type="warning"
          density="compact"
          class="mb-4"
        >
          Please run a calculation first before saving.
        </v-alert>

        <!-- Save error -->
        <v-alert
          v-if="saveError"
          type="error"
          density="compact"
          closable
          class="mb-4"
          @click:close="saveError = ''"
        >
          {{ saveError }}
        </v-alert>

        <form @submit.prevent="handleSave">
          <!-- Title Field -->
          <form-field
            id="save-calculation-title"
            v-model="form.title"
            type="text"
            label="Title"
            placeholder="e.g., Beam B-12 Final"
            :error-message="titleError"
            required
          />

          <!-- Project Dropdown -->
          <form-field
            id="save-calculation-project"
            v-model="form.projectId"
            type="select"
            label="Project"
            :options="projectOptions"
            :disabled="loadingProjects"
            :hint="lastUsedHint"
          />
        </form>
      </v-card-text>

      <v-card-actions class="dialog-actions">
        <v-spacer />
        <se-button
          variant="text"
          :disabled="saving"
          @click="handleCancel"
        >
          Cancel
        </se-button>
        <se-button
          variant="primary"
          :loading="saving"
          :disabled="!isValid || !props.calculationId"
          @click="handleSave"
        >
          Save
        </se-button>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import apiClient from '@/services/api'
import SeButton from '@/components/SEButton.vue'
import FormField from '@/components/ui/FormField.vue'

// Props
interface Props {
  modelValue: boolean
  calculationId?: string
  defaultTitle?: string
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: false,
  defaultTitle: ''
})

// Emits
const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  'saved': [data: { projectId: string; projectName: string; title: string }]
}>()

// Dialog state
const dialog = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

// Form state
const form = ref({
  title: props.defaultTitle || '',
  projectId: null as string | null
})

const saving = ref(false)
const saveError = ref('')
const loadingProjects = ref(false)

// Projects list
interface Project {
  id: string
  name: string
}

const projects = ref<Project[]>([])
const lastUsedProjectId = ref<string | null>(null)

const projectOptions = computed(() => {
  return [
    { value: '', title: 'Unsorted (No Project)' },
    ...projects.value.map((project) => ({ value: project.id, title: project.name })),
  ]
})

const lastUsedHint = computed(() => {
  if (!lastUsedProjectId.value) return ''
  const project = projects.value.find(p => p.id === lastUsedProjectId.value)
  return project ? `Last used: ${project.name}` : ''
})

const titleError = computed(() =>
  form.value.title.length > 100 ? 'Max 100 characters' : '',
)

const isValid = computed(() => {
  return form.value.title?.trim().length > 0 && form.value.title.length <= 100
})

// Fetch projects on mount
const fetchProjects = async () => {
  loadingProjects.value = true
  try {
    const response = await apiClient.get<Project[]>('/api/v1/projects')
    projects.value = response.data
  } catch (error) {
    console.error('Failed to fetch projects:', error)
  } finally {
    loadingProjects.value = false
  }
}

// Load last used project from localStorage
const loadLastUsedProject = () => {
  const stored = localStorage.getItem('structengine_last_project')
  if (stored) {
    lastUsedProjectId.value = stored
    form.value.projectId = stored
  }
}

// Save last used project
const saveLastUsedProject = (projectId: string | null) => {
  if (projectId) {
    localStorage.setItem('structengine_last_project', projectId)
    lastUsedProjectId.value = projectId
  }
}

// Handle save
const handleSave = async () => {
  if (!isValid.value || !props.calculationId) return

  saving.value = true
  saveError.value = ''
  try {
    await apiClient.patch(`/api/v1/history/${props.calculationId}`, {
      calculation_name: form.value.title.trim(),
      project_id: form.value.projectId
    })

    // Save last used project
    saveLastUsedProject(form.value.projectId)

    // Get project name for toast
    const projectName = form.value.projectId
      ? projects.value.find(p => p.id === form.value.projectId)?.name || 'Project'
      : 'Unsorted'

    emit('saved', {
      projectId: form.value.projectId || '',
      projectName,
      title: form.value.title
    })

    dialog.value = false
  } catch (error) {
    console.error('Failed to save calculation:', error)
    saveError.value = error instanceof Error
      ? error.message
      : 'Failed to save. Please try again.'
  } finally {
    saving.value = false
  }
}

// Handle cancel
const handleCancel = () => {
  dialog.value = false
}

// Reset form when dialog opens
watch(() => props.modelValue, (open) => {
  if (open) {
    form.value.title = props.defaultTitle || ''
    saveError.value = ''
    loadLastUsedProject()
    if (projects.value.length === 0) {
      fetchProjects()
    }
  }
})

onMounted(() => {
  loadLastUsedProject()
})
</script>

<style scoped>
.save-dialog {
  border-radius: var(--radius-lg);
}

.dialog-title {
  display: flex;
  align-items: center;
  padding: 20px 24px 12px;
  font-size: var(--font-size-xl);
  font-weight: 600;
}

.dialog-content {
  padding: 12px 24px 16px;
}

.dialog-actions {
  padding: 8px 16px 16px;
}
</style>
